import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
export default function ManageCourses() {
  const [courses, setCourses] = useState([]);
  const [editData, setEditData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [errors, setErrors] = useState({});
  const [tab, setTab] = useState("upload");
  const [dragOver, setDragOver] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("");
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  /* ─── Safe JSON parse ─── */
  const safeParse = (x, fallback = []) => {
    if (!x) return fallback;

    let data = x;
    let tries = 0;

    while (typeof data === "string" && tries++ < 5) {
      try {
        data = JSON.parse(data);
      } catch {
        break;
      }
    }

    if (Array.isArray(data)) return data;

    if (Array.isArray(data)) return data;

    if (typeof x === "string") {
      return x
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
    }

    return fallback;
  };

  /* ─── Normalize curriculum ───
     Handles ALL these malformed shapes from DB:
     1. Double-encoded string  '\"[{...}]\"'
     2. {module, lessons:[{title,duration,video}]}   ✅ correct
     3. {title, topics:[{name,duration}]}
     4. lessons where lesson.title is itself an object {title,duration,video}
  */
  const normalizeCurriculum = (raw) => {
    // Step 1: keep parsing strings until we get an array
    let data = raw;
    let safety = 0;
    while (typeof data === "string" && safety++ < 5) {
      try {
        data = JSON.parse(data);
      } catch {
        break;
      }
    }

    if (!Array.isArray(data) || data.length === 0) {
      return [
        { module: "", lessons: [{ title: "", duration: "", video: null }] },
      ];
    }

    return data.map((mod) => {
      const moduleName =
        typeof mod.module === "string"
          ? mod.module
          : typeof mod.title === "string"
            ? mod.title
            : "";

      const rawLessons = Array.isArray(mod.lessons)
        ? mod.lessons
        : Array.isArray(mod.topics)
          ? mod.topics
          : [];

      const lessons = rawLessons.map((l) => {
        // Fix: if l.title is itself an object {title,duration,video}, unwrap it
        const flat =
          l &&
          typeof l === "object" &&
          typeof l.title === "object" &&
          l.title !== null
            ? l.title
            : l;

        return {
          title: typeof flat?.title === "string" ? flat.title : "",
          duration: typeof flat?.duration === "string" ? flat.duration : "",
          video: null,

          topics: Array.isArray(flat?.topics) ? flat.topics.join(", ") : "",
          subtopics: Array.isArray(flat?.subtopics)
            ? flat.subtopics.join(", ")
            : "",
          assignment: flat?.assignment || "",
          example: flat?.example || "",
          videoId: flat?.videoId || "",
        };
      });

      return {
        module: moduleName,
        lessons: lessons.length
          ? lessons
          : [{ title: "", duration: "", video: null }],
      };
    });
  };

  const emptyForm = {
    title: "",
    description: "",
    category: "",
    duration: "",
    price: "",
    imageFile: null,
    type: "live",
    learn: [""],
    requirements: [""],
    tags: [],
    includes: [{ icon: "📚", value: "", label: "" }],
    reviews: [{ name: "", rating: "", text: "" }],
    instructor_name: "",
    instructor_role: "",
    instructor_bio: "",
    rating: 0,
  };

  const [form, setForm] = useState(emptyForm);
  const [curriculum, setCurriculum] = useState([
    {
      module: "",
      lessons: [
        {
          title: "",
          duration: "",
          video: null,

          // ✅ NEW FIELDS
          topics: "",
          subtopics: "",
          assignment: "",
          example: "",
          videoId: "",
        },
      ],
    },
  ]);

  const validate = () => {
    const err = {};
    if (!form.title.trim()) err.title = "Title required";
    if (!form.category.trim()) err.category = "Category required";
    if (!form.duration.trim()) err.duration = "Duration required";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  /* ─── Parse uploaded file ─── */
  const parseAndFill = async () => {
    if (!uploadFile) {
       Swal.fire({
  icon: "warning",
  title: "No File Selected",
  text: "Please select a file first",
});
      return false;
    }

    setUploadStatus("parsing");

    const fd = new FormData();
    fd.append("file", uploadFile);

    try {
      const res = await fetch("https://swiftintelli.com/api/parseCourse.php", {
        method: "POST",
        body: fd,
      });

      if (!res.ok) {
        Swal.fire({
  icon: "error",
  title: "Server Error",
  text: "Server error (500)",
});
        setUploadStatus("error");
        return false;
      }

      const text = await res.text();
      console.log("RAW:", text);

      let data;
      try {
        data = JSON.parse(text);
      } catch {
        Swal.fire({
  icon: "error",
  title: "Invalid Response",
  text: "Server returned invalid data",
});
        setUploadStatus("error");
        return false;
      }

      if (data.status === "error") {
        alert(data.message);
        setUploadStatus("error");
        return false;
      }

      // ✅ SUCCESS
      setForm((prev) => ({
        ...prev,
        title: data.title || "",
        description: data.description || "",
        category: data.category || "",
        duration: data.duration || "",
        price: data.price || "",
        type: data.type || "live",
        learn: data.learn || [""],
        requirements: data.requirements || [""],
        tags: data.tags || [],
        includes: data.includes || [],
        reviews: data.reviews || [],
        instructor_name: data.instructor_name || "",
        instructor_role: data.instructor_role || "",
        instructor_bio: data.instructor_bio || "",
        rating: data.rating || 0,
      }));

      setCurriculum(normalizeCurriculum(data.curriculum));
      setTab("manual");
      setUploadStatus("done");

      return true;
    } catch (err) {
      console.error(err);
      Swal.fire({
  icon: "error",
  title: "Parsing Failed",
  text: "Unable to parse uploaded file",
});
      setUploadStatus("error");
      return false;
    }
  };

  useEffect(() => {
    fetch("https://swiftintelli.com/api/getCourses.php")
      .then((r) => r.json())
      .then((d) => setCourses(d || []));
  }, []);

  /* ─── Save course ─── */
  const saveCourse = async () => {
    if (saving) return; // 🚫 prevent multiple clicks

    if (!validate()) return;

    setSaving(true);

    const fd = new FormData();

    if (curriculum && curriculum.length) {
      fd.append("curriculum", JSON.stringify(curriculum));
    }

    Object.keys(form).forEach((key) => {
      if (key === "imageFile") {
        if (form.imageFile) fd.append("imageFile", form.imageFile);
      } else if (
        ["learn", "requirements", "tags", "includes", "reviews"].includes(
          key,
        ) &&
        form[key]?.length
      ) {
        fd.append(key, JSON.stringify(form[key]));
      } else {
        fd.append(key, form[key] ?? "");
      }
    });

    const url = isEdit
      ? "https://swiftintelli.com/api/updateCourse.php"
      : "https://swiftintelli.com/api/addCourse.php";

    if (isEdit) fd.append("id", editData.id);

    try {
      const res = await fetch(url, { method: "POST", body: fd });
      const text = await res.text();
      console.log(text);
      const data = JSON.parse(text);

      if (data.status === "success") {
        Swal.fire({
  icon: "success",
  title: "Success",
  text: isEdit
    ? "Course updated successfully"
    : "Course added successfully",
  timer: 1800,
  showConfirmButton: false,
});

        setShowModal(false);

        // ✅ IMPORTANT FIX
        if (!isEdit) {
          setForm(emptyForm); // reset only when adding
        }

        setIsEdit(false);

        fetch("https://swiftintelli.com/api/getCourses.php")
          .then((r) => r.json())
          .then((d) => setCourses(d));
      } else {
        Swal.fire({
  icon: "error",
  title: "Failed",
  text: data.message || "Unknown error",
});
      }
    } catch (err) {
      Swal.fire({
  icon: "error",
  title: "Server Error",
  text: "Something went wrong",
});
      console.error(err);
    }

    setSaving(false);
  };

  const deleteCourse = async (id) => {
  const result = await Swal.fire({
  title: "Delete Course?",
  text: "This action cannot be undone",
  icon: "warning",
  showCancelButton: true,
  confirmButtonColor: "#ef4444",
  cancelButtonColor: "#6b7280",
  confirmButtonText: "Yes, Delete",
});

if (!result.isConfirmed) return;
    await fetch(`https://swiftintelli.com/api/deleteCourse.php?id=${id}`);
    setCourses(courses.filter((c) => c.id !== id));
  };

  /* ─── Open Edit modal ─── */
  const handleEdit = async (c) => {
    try {
      const res = await fetch(
        `https://swiftintelli.com/api/getCourse.php?id=${c.id}`,
      );
      const data = await res.json();

      console.log("FULL DATA:", data);

      setEditData(data);
      setIsEdit(true);
      setTab("manual");
      setShowModal(true);

      // ✅ curriculum
      setCurriculum(normalizeCurriculum(data.curriculum));

      // ✅ form
      setForm({
        title: data.title || "",
        description: data.description || "",
        category: data.category || "",
        duration: data.duration || "",
        price: data.price || "",
        type: data.type || "live",

        learn: data.learn || [""],
        requirements: data.requirements || [""],
        includes: data.includes || [{ icon: "", value: "", label: "" }],
        reviews: data.reviews || [{ name: "", rating: "", text: "" }],
        tags: data.tags || [],

        instructor_name: data.instructor?.name || "",
        instructor_role: data.instructor?.role || "",
        instructor_bio: data.instructor?.bio || "",

        rating: data.rating || 0,
        imageFile: null,
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
  icon: "error",
  title: "Load Failed",
  text: "Failed to load course data",
});
    }
  };

  const openAdd = () => {
    setIsEdit(false);
    setEditData(null);
    setTab("upload");
    setShowModal(true);
    setForm(emptyForm);
    setCurriculum([
      { module: "", lessons: [{ title: "", duration: "", video: null }] },
    ]);
    setUploadFile(null);
    setUploadStatus("");
  };

  /* ─── Curriculum helpers ─── */
  const updateModule = (i, v) => {
    const u = [...curriculum];
    u[i].module = v;
    setCurriculum(u);
  };
  const updateLesson = (mi, li, f, v) => {
    const u = [...curriculum];
    u[mi].lessons[li][f] = v;
    setCurriculum(u);
  };
  const addLesson = (mi) => {
    const u = [...curriculum];
    u[mi].lessons.push({
      title: "",
      duration: "",
      video: null,

      topics: "",
      subtopics: "",
      assignment: "",
      example: "",
      videoId: "",
    });
    setCurriculum(u);
  };
  const addModule = () =>
    setCurriculum([
      ...curriculum,
      { module: "", lessons: [{ title: "", duration: "", video: null }] },
    ]);
  const deleteModule = (i) => {
    const u = [...curriculum];
    u.splice(i, 1);
    setCurriculum(u);
  };
  const deleteLesson = (mi, li) => {
    const u = [...curriculum];
    u[mi].lessons.splice(li, 1);
    setCurriculum(u);
  };

  const categoryGradients = {
    "AI / ML": "linear-gradient(135deg,#1a1040,#4f46e5)",
    "Full Stack": "linear-gradient(135deg,#0a2540,#0891b2)",
    Frontend: "linear-gradient(135deg,#0a1a40,#4f46e5)",
    Python: "linear-gradient(135deg,#0f2610,#16a34a)",
    DevOps: "linear-gradient(135deg,#2a1000,#ea580c)",
    Design: "linear-gradient(135deg,#2a0a1a,#db2777)",
    Mobile: "linear-gradient(135deg,#2a1a00,#d97706)",
    "Web Development": "linear-gradient(135deg,#0a2030,#0e7490)",
    "Data Science": "linear-gradient(135deg,#1a0a30,#7c3aed)",
    Cybersecurity: "linear-gradient(135deg,#0a1a10,#059669)",
  };
  const getGradient = (cat) =>
    categoryGradients[cat] || "linear-gradient(135deg,#1e1b4b,#4f46e5)";

  return (
    <div className="mc-root">
      <Navbar />
      <div className="mc-page">
        <div className="mc-header">
          <div className="mc-header-left">
            <h1 className="mc-title">Manage Courses</h1>
            <p className="mc-subtitle">{courses.length} courses published</p>
          </div>
          <button className="mc-btn-save" disabled={saving} onClick={openAdd}>
            + Add Course
          </button>
        </div>

        {/* ── COURSE GRID ── */}
        <div className="mc-grid">
          {courses.map((c) => (
            <div key={c.id} className="mc-card">
              {/* 🔥 IMAGE / HEADER */}
              <div
                className="mc-card-visual"
                style={{ background: getGradient(c.category) }}
              >
                {/* IMAGE */}
                {c.image && (
                  <img
                    src={
                      c.image?.startsWith("http")
                        ? c.image
                        : `https://swiftintelli.com/api/uploads/${c.image}`
                    }
                    alt={c.title}
                    className="mc-card-img"
                  />
                )}

                <div className="mc-card-overlay" />

                {/* ✅ CATEGORY (LEFT TOP) */}
                <span className="mc-badge-left">{c.category || "General"}</span>

                {/* ✅ STATUS (RIGHT TOP) */}
                <span
                  className={`mc-badge-right ${c.type === "live" ? "live" : "soon"}`}
                >
                  {c.type === "live" ? "● Live" : "● Soon"}
                </span>
              </div>

              {/* 🔥 BODY */}
              <div className="mc-card-body">
                {/* ✅ TITLE (FIXED) */}
                <h3 className="mc-title">{c.name || c.title || "Course"}</h3>

                {/* ✅ DESCRIPTION CLEAN */}
                <p className="mc-card-desc">
                  {c.description?.split("Category:")[0]}
                </p>

                {/* ✅ DURATION */}
                <div className="mc-card-footer">
                  <span className="mc-dur-tag">⏱ {c.duration || "—"}</span>
                </div>
              </div>

              {/* 🔥 ACTIONS */}
              <div className="mc-card-actions">
                <button className="mc-btn-edit" onClick={() => handleEdit(c)}>
                  <EditIcon /> Edit
                </button>

                <button
                  className="mc-btn-delete"
                  onClick={() => deleteCourse(c.id)}
                >
                  <TrashIcon /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="mc-overlay">
          <div className="mc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mc-modal-head">
              <div>
                <h2 className="mc-modal-title">
                  {isEdit ? "Edit Course" : "Add New Course"}
                </h2>
                <p className="mc-modal-sub">SwiftIntelli Learning Platform</p>
              </div>
              <button className="mc-close" onClick={() => setShowModal(false)}>
                ✕
              </button>
            </div>

            <div className="mc-tab-row">
              <button
                className={`mc-tab ${tab === "upload" ? "active" : ""}`}
                onClick={() => setTab("upload")}
              >
                📄 Auto Fill from File
              </button>
              <button
                className={`mc-tab ${tab === "manual" ? "active" : ""}`}
                onClick={() => setTab("manual")}
              >
                ✏️ Manual Entry
              </button>
            </div>

            <div className="mc-modal-body">
              {/* ── UPLOAD TAB ── */}
              {tab === "upload" && (
                <div className="mc-upload-panel">
                  <div
                    className={`mc-drop-zone ${dragOver ? "drag-active" : ""} ${uploadFile ? "has-file" : ""}`}
                    onClick={() =>
                      document.getElementById("autoFillInput").click()
                    }
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOver(false);
                      const f = e.dataTransfer.files[0];
                      if (f) {
                        setUploadFile(f);
                        setUploadStatus("ready");
                      }
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                  >
                    {!uploadFile ? (
                      <>
                        <div className="mc-drop-icon">
                          <UploadIcon />
                        </div>
                        <p className="mc-drop-title">Drop your files here!</p>
                        <p className="mc-drop-or">or click</p>
                        <button
                          className="mc-upload-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById("autoFillInput").click();
                          }}
                        >
                          + Add Files
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="mc-file-ready-icon">✅</div>
                        <p className="mc-file-name">{uploadFile.name}</p>
                        <p className="mc-file-size">
                          {(uploadFile.size / 1024).toFixed(1)} KB
                        </p>
                        <button
                          className="mc-remove-file"
                          onClick={(e) => {
                            e.stopPropagation();
                            setUploadFile(null);
                            setUploadStatus("");
                          }}
                        >
                          ✕ Remove
                        </button>
                        {uploadStatus === "parsing" && (
                          <p className="mc-parse-status">⏳ Parsing...</p>
                        )}
                        {uploadStatus === "error" && (
                          <p className="mc-parse-status error">
                            ❌ Failed — try again
                          </p>
                        )}
                      </>
                    )}
                    <input
                      id="autoFillInput"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => {
                        const f = e.target.files[0];
                        if (f) {
                          setUploadFile(f);
                          setUploadStatus("ready");
                        }
                      }}
                      hidden
                    />
                  </div>
                </div>
              )}

              {/* ── MANUAL TAB ── */}
              {tab === "manual" && (
                <div className="mc-form">
                  <Section label="Basic Information">
                    <div className="mc-form-grid">
                      <div className="mc-field full">
                        <label>
                          Course Title{" "}
                          {errors.title && (
                            <span className="mc-err">{errors.title}</span>
                          )}
                        </label>
                        <input
                          className={`mc-input ${errors.title ? "has-error" : ""}`}
                          placeholder="e.g. React Full Stack Course"
                          value={form.title}
                          onChange={(e) =>
                            setForm({ ...form, title: e.target.value })
                          }
                        />
                      </div>
                      <div className="mc-field">
                        <label>
                          Category{" "}
                          {errors.category && (
                            <span className="mc-err">{errors.category}</span>
                          )}
                        </label>
                        <select
                          className={`mc-input mc-select ${errors.category ? "has-error" : ""}`}
                          value={form.category}
                          onChange={(e) =>
                            setForm({ ...form, category: e.target.value })
                          }
                        >
                          <option value="">Select a category</option>
                          <option value="AI / ML">AI / ML</option>
                          <option value="Full Stack">Full Stack</option>
                          <option value="Frontend">Frontend</option>
                          <option value="Python">Python</option>
                          <option value="DevOps">DevOps</option>
                          <option value="Design">Design</option>
                          <option value="Mobile">Mobile</option>
                          <option value="Web Development">
                            Web Development
                          </option>
                          <option value="Data Science">Data Science</option>
                          <option value="Cybersecurity">Cybersecurity</option>
                        </select>
                      </div>
                      <div className="mc-field">
                        <label>
                          Duration{" "}
                          {errors.duration && (
                            <span className="mc-err">{errors.duration}</span>
                          )}
                        </label>
                        <input
                          className={`mc-input ${errors.duration ? "has-error" : ""}`}
                          placeholder="e.g. 40 hours"
                          value={form.duration}
                          onChange={(e) =>
                            setForm({ ...form, duration: e.target.value })
                          }
                        />
                      </div>
                      <div className="mc-field">
                        <label>Price (₹)</label>
                        <input
                          className="mc-input"
                          placeholder="e.g. 4999"
                          value={form.price}
                          onChange={(e) =>
                            setForm({ ...form, price: e.target.value })
                          }
                        />
                      </div>
                      <div className="mc-field">
                        <label>Rating (0–5)</label>
                        <input
                          className="mc-input"
                          type="number"
                          min="0"
                          max="5"
                          step="0.1"
                          placeholder="4.5"
                          value={form.rating}
                          onChange={(e) =>
                            setForm({ ...form, rating: e.target.value })
                          }
                        />
                      </div>
                      <div className="mc-field">
                        <label>Enrollment Status</label>
                        <select
                          className="mc-input mc-select"
                          value={form.type}
                          onChange={(e) =>
                            setForm({ ...form, type: e.target.value })
                          }
                        >
                          <option value="live">🟢 Enroll Now</option>
                          <option value="upcoming">🟡 Notify Me</option>
                        </select>
                      </div>
                      <div className="mc-field full">
                        <label>Thumbnail Image</label>
                        <input
                          className="mc-input"
                          type="file"
                          accept="image/*"
                          onChange={(e) =>
                            setForm({ ...form, imageFile: e.target.files[0] })
                          }
                        />
                      </div>
                      <div className="mc-field full">
                        <label>Description</label>
                        <textarea
                          className="mc-input mc-textarea"
                          placeholder="What will students learn and achieve..."
                          value={form.description}
                          onChange={(e) =>
                            setForm({ ...form, description: e.target.value })
                          }
                        />
                      </div>
                      <div className="mc-field full">
                        <label>Tags (comma separated)</label>
                        <input
                          className="mc-input"
                          placeholder="Python, AI, Machine Learning"
                          value={form.tags.join(",")}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              tags: e.target.value.split(","),
                            })
                          }
                        />
                      </div>
                    </div>
                  </Section>

                  <Section label="What You'll Learn">
                    {form.learn.map((l, i) => (
                      <div key={i} className="mc-list-row">
                        <span className="mc-list-num">{i + 1}</span>
                        <input
                          className="mc-input"
                          placeholder="Learning outcome..."
                          value={l}
                          onChange={(e) => {
                            const a = [...form.learn];
                            a[i] = e.target.value;
                            setForm({ ...form, learn: a });
                          }}
                        />
                        {form.learn.length > 1 && (
                          <button
                            className="mc-x-btn"
                            onClick={() => {
                              const a = [...form.learn];
                              a.splice(i, 1);
                              setForm({ ...form, learn: a });
                            }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      className="mc-ghost-btn"
                      onClick={() =>
                        setForm({ ...form, learn: [...form.learn, ""] })
                      }
                    >
                      + Add Item
                    </button>
                  </Section>

                  <Section label="Requirements">
                    {form.requirements.map((r, i) => (
                      <div key={i} className="mc-list-row">
                        <span className="mc-list-num">{i + 1}</span>
                        <input
                          className="mc-input"
                          placeholder="Prerequisite..."
                          value={r}
                          onChange={(e) => {
                            const a = [...form.requirements];
                            a[i] = e.target.value;
                            setForm({ ...form, requirements: a });
                          }}
                        />
                        {form.requirements.length > 1 && (
                          <button
                            className="mc-x-btn"
                            onClick={() => {
                              const a = [...form.requirements];
                              a.splice(i, 1);
                              setForm({ ...form, requirements: a });
                            }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      className="mc-ghost-btn"
                      onClick={() =>
                        setForm({
                          ...form,
                          requirements: [...form.requirements, ""],
                        })
                      }
                    >
                      + Add Item
                    </button>
                  </Section>

                  <Section label="Course Curriculum">
                    {curriculum.map((mod, i) => {
                      const safeModule =
                        typeof mod.module === "string" ? mod.module : "";
                      const safeLessons = Array.isArray(mod.lessons)
                        ? mod.lessons
                        : [];

                      return (
                        <div key={i} className="mc-module">
                          {/* MODULE HEADER */}
                          <div className="mc-module-head">
                            <span className="mc-mod-badge">M{i + 1}</span>

                            <input
                              className="mc-input mc-mod-input"
                              placeholder="Module title..."
                              value={safeModule}
                              onChange={(e) => updateModule(i, e.target.value)}
                            />

                            {curriculum.length > 1 && (
                              <button
                                className="mc-x-btn"
                                onClick={() => deleteModule(i)}
                              >
                                ✕
                              </button>
                            )}
                          </div>

                          {/* LESSONS */}
                          <div className="mc-lessons">
                            {safeLessons.map((lesson, j) => {
                              const safeTitle = lesson?.title || "";
                              const safeDuration = lesson?.duration || "";
                              const safeTopics = lesson?.topics || "";
                              const safeSubtopics = lesson?.subtopics || "";
                              const safeAssignment = lesson?.assignment || "";
                              const safeExample = lesson?.example || "";
                              const safeVideoId = lesson?.videoId || "";

                              return (
                                <div key={j} className="mc-lesson">
                                  {/* TITLE + DURATION */}
                                  <div className="mc-lesson-row">
                                    <span className="mc-dot" />

                                    <input
                                      className="mc-input"
                                      placeholder="Lesson title..."
                                      value={safeTitle}
                                      onChange={(e) =>
                                        updateLesson(
                                          i,
                                          j,
                                          "title",
                                          e.target.value,
                                        )
                                      }
                                    />

                                    <input
                                      className="mc-input mc-dur-input"
                                      placeholder="Duration"
                                      value={safeDuration}
                                      onChange={(e) =>
                                        updateLesson(
                                          i,
                                          j,
                                          "duration",
                                          e.target.value,
                                        )
                                      }
                                    />

                                    {safeLessons.length > 1 && (
                                      <button
                                        className="mc-x-btn"
                                        onClick={() => deleteLesson(i, j)}
                                      >
                                        ✕
                                      </button>
                                    )}
                                  </div>

                                  {/* TOPICS */}
                                  <input
                                    className="mc-input"
                                    placeholder="Topics (comma separated)"
                                    value={safeTopics}
                                    onChange={(e) =>
                                      updateLesson(
                                        i,
                                        j,
                                        "topics",
                                        e.target.value,
                                      )
                                    }
                                  />

                                  {/* SUBTOPICS */}
                                  <input
                                    className="mc-input"
                                    placeholder="Subtopics (comma separated)"
                                    value={safeSubtopics}
                                    onChange={(e) =>
                                      updateLesson(
                                        i,
                                        j,
                                        "subtopics",
                                        e.target.value,
                                      )
                                    }
                                  />

                                  {/* ASSIGNMENT */}
                                  <input
                                    className="mc-input"
                                    placeholder="Assignment"
                                    value={safeAssignment}
                                    onChange={(e) =>
                                      updateLesson(
                                        i,
                                        j,
                                        "assignment",
                                        e.target.value,
                                      )
                                    }
                                  />

                                  {/* EXAMPLE */}
                                  <input
                                    className="mc-input"
                                    placeholder="Example"
                                    value={safeExample}
                                    onChange={(e) =>
                                      updateLesson(
                                        i,
                                        j,
                                        "example",
                                        e.target.value,
                                      )
                                    }
                                  />

                                  {/* VIDEO ID */}
                                  <input
                                    className="mc-input"
                                    placeholder="Video ID (optional)"
                                    value={safeVideoId}
                                    onChange={(e) =>
                                      updateLesson(
                                        i,
                                        j,
                                        "videoId",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </div>
                              );
                            })}
                          </div>

                          <button
                            className="mc-ghost-btn small"
                            onClick={() => addLesson(i)}
                          >
                            + Add Topic
                          </button>
                        </div>
                      );
                    })}

                    <button className="mc-ghost-btn" onClick={addModule}>
                      + Add Module
                    </button>
                  </Section>

                  <Section label="Course Includes">
                    {form.includes.map((inc, i) => (
                      <div key={i} className="mc-includes-row">
                        <input
                          className="mc-input mc-icon-inp"
                          placeholder="Icon"
                          value={inc.icon || ""}
                          onChange={(e) => {
                            const a = [...form.includes];
                            a[i] = { ...a[i], icon: e.target.value };
                            setForm({ ...form, includes: a });
                          }}
                        />
                        <input
                          className="mc-input"
                          placeholder="Value"
                          value={inc.value || ""}
                          onChange={(e) => {
                            const a = [...form.includes];
                            a[i] = { ...a[i], value: e.target.value };
                            setForm({ ...form, includes: a });
                          }}
                        />
                        <input
                          className="mc-input"
                          placeholder="Label"
                          value={inc.label || ""}
                          onChange={(e) => {
                            const a = [...form.includes];
                            a[i] = { ...a[i], label: e.target.value };
                            setForm({ ...form, includes: a });
                          }}
                        />
                        {form.includes.length > 1 && (
                          <button
                            className="mc-x-btn"
                            onClick={() => {
                              const a = [...form.includes];
                              a.splice(i, 1);
                              setForm({ ...form, includes: a });
                            }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      className="mc-ghost-btn"
                      onClick={() =>
                        setForm({
                          ...form,
                          includes: [
                            ...form.includes,
                            { icon: "", value: "", label: "" },
                          ],
                        })
                      }
                    >
                      + Add Item
                    </button>
                  </Section>

                  <Section label="Student Reviews">
                    {form.reviews.map((r, i) => (
                      <div key={i} className="mc-review-block">
                        <div className="mc-form-grid">
                          <div className="mc-field">
                            <label>Reviewer Name</label>
                            <input
                              className="mc-input"
                              placeholder="Name"
                              value={r.name || ""}
                              onChange={(e) => {
                                const a = [...form.reviews];
                                a[i] = { ...a[i], name: e.target.value };
                                setForm({ ...form, reviews: a });
                              }}
                            />
                          </div>
                          <div className="mc-field">
                            <label>Rating (1–5)</label>
                            <input
                              className="mc-input"
                              type="number"
                              min="1"
                              max="5"
                              placeholder="5"
                              value={r.rating || ""}
                              onChange={(e) => {
                                const a = [...form.reviews];
                                a[i] = { ...a[i], rating: e.target.value };
                                setForm({ ...form, reviews: a });
                              }}
                            />
                          </div>
                          <div className="mc-field full">
                            <label>Review Text</label>
                            <textarea
                              className="mc-input mc-textarea"
                              placeholder="Review..."
                              value={r.text || ""}
                              onChange={(e) => {
                                const a = [...form.reviews];
                                a[i] = { ...a[i], text: e.target.value };
                                setForm({ ...form, reviews: a });
                              }}
                            />
                          </div>
                        </div>
                        {form.reviews.length > 1 && (
                          <button
                            className="mc-ghost-btn danger small"
                            onClick={() => {
                              const a = [...form.reviews];
                              a.splice(i, 1);
                              setForm({ ...form, reviews: a });
                            }}
                          >
                            Remove Review
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      className="mc-ghost-btn"
                      onClick={() =>
                        setForm({
                          ...form,
                          reviews: [
                            ...form.reviews,
                            { name: "", rating: "", text: "" },
                          ],
                        })
                      }
                    >
                      + Add Review
                    </button>
                  </Section>

                  <Section label="Instructor Details">
                    <div className="mc-form-grid">
                      <div className="mc-field">
                        <label>Name</label>
                        <input
                          className="mc-input"
                          placeholder="Full name"
                          value={form.instructor_name}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              instructor_name: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="mc-field">
                        <label>Role / Title</label>
                        <input
                          className="mc-input"
                          placeholder="e.g. Senior Developer"
                          value={form.instructor_role}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              instructor_role: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="mc-field full">
                        <label>Bio</label>
                        <textarea
                          className="mc-input mc-textarea"
                          placeholder="Short bio..."
                          value={form.instructor_bio}
                          onChange={(e) =>
                            setForm({ ...form, instructor_bio: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  </Section>
                </div>
              )}
            </div>

            <div className="mc-modal-foot">
              <button
                className="mc-btn-cancel"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                className="mc-btn-save"
                disabled={tab === "upload" && uploadStatus === "parsing"}
                onClick={async () => {
                  if (tab === "upload") {
                    const success = await parseAndFill();

                    if (!success) return; // ❌ STOP if parsing failed
                  }

                  await saveCourse();
                }}
              >
                {tab === "upload"
                  ? uploadStatus === "parsing"
                    ? "Uploading..."
                    : "Add Course"
                  : isEdit
                    ? "Save Changes"
                    : "Add Course"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div className="mc-section">
      <div className="mc-section-label">{label}</div>
      {children}
    </div>
  );
}
function EditIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}
function UploadIcon() {
  return (
    <svg
      width="36"
      height="36"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}
